// ONE back behaviour for the whole app (user call 2026-09-08: "if the user
// goes to a brand page and then to the AI Lab and clicks back, he ends up on
// the home page instead of the brand page — make navigation consistent").
//
// Every screen used to send its back control to a HARDCODED parent
// (`history.push("/")` from the Lab, `push("/brands/:slug")` from the
// advisor…), which broke twice over: it ignored where the reader actually
// came from, and a push made the stack GROW on the way back — so the
// browser's own back button then walked FORWARD into the screen they had
// just left, and any board scope (`?list=`, `?industry=`) was dropped on the
// floor.
//
// The rule now: a back control means "return to where I came from", which is
// `history.goBack()` — but only when the entry behind us is OURS. On a cold
// deep link (a shared URL, a new tab, a refresh, or after a guard's
// `replace`) there is nothing of this app behind the current entry and
// goBack would leave the site, so the control goes UP to the screen's parent
// instead — the `fallback` each caller passes — and REPLACES rather than
// pushes, so a back control can never grow the stack.
//
// "Is there an app entry behind us" is COUNTED, not guessed. `history.action`
// cannot answer it, because a REPLACE (the industry filters, the Lab clearing
// its `ask`, the Compare swap) erases how we arrived; `window.history.length`
// counts entries this app never owned. So RouterConfig subscribes
// `trackBackDepth` once and every PUSH deepens the count, every POP shallows
// it. A browser FORWARD is also a POP, so the count can drift low after
// back-then-forward — deliberately the safe direction: an under-count only
// sends the reader UP a level, never off the site.
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

let depth = 0;
let listening = false;

// subscribe once, at the router (RouterConfig) — returns the unsubscriber,
// which is also what React wants back from an effect
export function trackBackDepth(history) {
  listening = true;
  const unlisten = history.listen((_location, action) => {
    if (action === "PUSH") depth += 1;
    else if (action === "POP") depth = Math.max(0, depth - 1);
  });
  return () => {
    listening = false;
    unlisten();
  };
}

// a push made while NO router is subscribed — the gate's hop onto a link's
// answer, made behind the wall's fallback with the home put under it
// (components/AuthGate, 2026-10-14) — counts itself, so the answer's own
// back control walks back to that home instead of replacing over it; a
// subscribed router counts it like any push
export function pushOwn(history, to) {
  history.push(to);
  if (!listening) depth += 1;
}

// is the entry behind the current one this app's?
export function canGoBack() {
  return depth > 0;
}

// tests only: each case starts from a cold load
export function resetBackDepth() {
  depth = 0;
  listening = false;
}

// `fallback` is the screen's parent in the URL hierarchy — where "up" goes
// when there is no "back" (Lab and Advisor → their brand page, Compare → its
// left brand, BrandDetail and Stats → the board).
export function useBack(fallback) {
  const history = useHistory();
  return useCallback(() => {
    if (canGoBack()) history.goBack();
    else history.replace(fallback);
  }, [history, fallback]);
}
